import { useState } from "react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

const REASON_LABELS = {
  price: "Price / budget",
  timing: "Timing not right",
  competitor: "Chose a competitor",
  no_response: "Went cold — no response",
  not_a_fit: "Not a good fit",
  other: "Other",
};

/**
 * MarkLostDialog — moves a lead to 'lost' (or a client to 'churned')
 * and captures a reason. Called from Lead/Client detail views.
 */
export default function MarkLostDialog({ lead, reasons, onClose, onMarked }) {
  const [reason, setReason] = useState(reasons?.[0] || "price");
  const [saving, setSaving] = useState(false);
  const isClient = lead.kind === "client";

  async function submit() {
    setSaving(true);
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/mark-lost`, { lost_reason: reason });
      toast.success(isClient ? `${lead.org_name} marked churned` : `${lead.org_name} marked lost`);
      onMarked(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mark-lost-dialog">
        <DialogHeader>
          <DialogTitle>{isClient ? "Mark as churned" : "Mark as lost"}</DialogTitle>
          <DialogDescription>{lead.org_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="text-xs uppercase tracking-[0.2em] text-slate-500">
            Reason
          </label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="lost-reason-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(reasons || []).map((r) => (
                <SelectItem key={r} value={r} data-testid={`reason-${r}`}>
                  {REASON_LABELS[r] || r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="mark-lost-confirm"
          >
            {saving ? "Saving…" : isClient ? "Mark churned" : "Mark lost"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
